import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentViewer = ({ document, onSignaturePlace, signatures, currentUser }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isPlacingSignature, setIsPlacingSignature] = useState(false);
  const [signaturePosition, setSignaturePosition] = useState(null);
  const viewerRef = useRef(null);

  const totalPages = document?.totalPages || 12;
  const canSign = document?.status === 'pending_signature' && !signatures?.find(s => s?.userId === currentUser?.id);

  const handleZoomIn = () => {
    if (zoomLevel < 200) {
      setZoomLevel(prev => Math.min(prev + 25, 200));
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 50) {
      setZoomLevel(prev => Math.max(prev - 25, 50));
    }
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleDocumentClick = (event) => {
    if (!isPlacingSignature) return;

    const rect = event?.currentTarget?.getBoundingClientRect();
    const x = ((event?.clientX - rect?.left) / rect?.width) * 100;
    const y = ((event?.clientY - rect?.top) / rect?.height) * 100;

    setSignaturePosition({ x, y, page: currentPage });
    setIsPlacingSignature(false);
  };

  const handlePlaceSignature = () => {
    if (signaturePosition && onSignaturePlace) {
      onSignaturePlace({
        ...signaturePosition,
        userId: currentUser?.id,
        timestamp: new Date()?.toISOString()
      });
      setSignaturePosition(null);
    }
  };

  const getSignatureRequirements = () => {
    const requirements = [
      { page: 3, x: 70, y: 85, label: 'Property Owner Signature', required: true },
      { page: 5, x: 30, y: 90, label: 'Legal Representative', required: true },
      { page: 8, x: 50, y: 80, label: 'Notary Signature', required: true },
      { page: 12, x: 60, y: 85, label: 'Final Acknowledgment', required: true }
    ];
    return requirements;
  };

  const signatureRequirements = getSignatureRequirements();
  const currentPageRequirements = signatureRequirements?.filter(req => req?.page === currentPage);

  return (
    <div className="flex flex-col h-full bg-card border border-border rounded-lg overflow-hidden">
      {/* Document Viewer Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-3">
          <Icon name="FileText" size={20} className="text-primary" />
          <div>
            <h3 className="font-medium text-foreground">{document?.name}</h3>
            <p className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} • {document?.size}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 bg-background border border-border rounded-md">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 50}
            >
              <Icon name="ZoomOut" size={16} />
            </Button>
            <span className="px-2 text-sm font-medium text-foreground min-w-[60px] text-center">
              {zoomLevel}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoomLevel >= 200}
            >
              <Icon name="ZoomIn" size={16} />
            </Button>
          </div>

          {canSign && (
            <Button
              variant={isPlacingSignature ? "default" : "outline"}
              size="sm"
              onClick={() => setIsPlacingSignature(!isPlacingSignature)}
              iconName="PenTool"
              iconPosition="left"
            >
              {isPlacingSignature ? 'Click to Place' : 'Add Signature'}
            </Button>
          )}
        </div>
      </div>
      {/* Document Content Area */}
      <div className="flex-1 relative overflow-auto bg-muted/10">
        <div 
          ref={viewerRef}
          className={`relative mx-auto bg-white shadow-soft ${
            isPlacingSignature ? 'cursor-crosshair' : 'cursor-default'
          }`}
          style={{
            width: `${(8.5 * zoomLevel) / 100 * 96}px`,
            height: `${(11 * zoomLevel) / 100 * 96}px`,
            transform: `scale(${zoomLevel / 100})`,
            transformOrigin: 'top center',
            margin: '20px auto'
          }}
          onClick={handleDocumentClick}
        >
          {/* Document Background */}
          <div className="w-full h-full bg-white border border-border relative">
            {/* Mock Document Content */}
            <div className="p-8 space-y-6">
              <div className="text-center border-b border-border pb-4">
                <h1 className="text-2xl font-bold text-foreground">PROPERTY PURCHASE AGREEMENT</h1>
                <p className="text-sm text-muted-foreground mt-2">Transaction ID: TXN-2025-001</p>
              </div>

              {currentPage === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-foreground">Property Address:</p>
                      <p className="text-muted-foreground">123 Blockchain Avenue\nCrypto City, CC 12345</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Purchase Price:</p>
                      <p className="text-muted-foreground">$2,500,000 USD</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-foreground">This agreement is entered into between the following parties:</p>
                    <p className="text-muted-foreground">Buyer: PropertyChain Investment LLC</p>
                    <p className="text-muted-foreground">Seller: Digital Real Estate Holdings Inc.</p>
                  </div>
                </div>
              )}

              {currentPage === 3 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-foreground">Terms and Conditions</h2>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>1. The property shall be transferred free and clear of all liens and encumbrances.</p>
                    <p>2. Closing shall occur within 30 days of all signatures being obtained.</p>
                    <p>3. All blockchain timestamps shall serve as legal proof of execution.</p>
                  </div>
                </div>
              )}

              {/* Signature Requirements Indicators */}
              {currentPageRequirements?.map((req, index) => (
                <div
                  key={index}
                  className="absolute border-2 border-dashed border-primary bg-primary/5 rounded"
                  style={{
                    left: `${req?.x}%`,
                    top: `${req?.y}%`,
                    width: '200px',
                    height: '60px',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Icon name="PenTool" size={16} className="text-primary mx-auto mb-1" />
                      <p className="text-xs font-medium text-primary">{req?.label}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Existing Signatures */}
              {signatures?.filter(sig => sig?.page === currentPage)?.map((sig, index) => (
                  <div
                    key={index}
                    className="absolute bg-success/10 border-2 border-success rounded p-2"
                    style={{
                      left: `${sig?.x}%`,
                      top: `${sig?.y}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <div className="text-xs">
                        <p className="font-medium text-success">{sig?.userName}</p>
                        <p className="text-muted-foreground">
                          {new Date(sig.timestamp)?.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Pending Signature Placement */}
              {signaturePosition && signaturePosition?.page === currentPage && (
                <div
                  className="absolute bg-warning/10 border-2 border-warning rounded p-2"
                  style={{
                    left: `${signaturePosition?.x}%`,
                    top: `${signaturePosition?.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} className="text-warning" />
                    <div className="text-xs">
                      <p className="font-medium text-warning">Pending Signature</p>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handlePlaceSignature}
                        className="mt-1"
                      >
                        Confirm
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Signature Placement Instructions */}
        {isPlacingSignature && (
          <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-elevated">
            <div className="flex items-center space-x-2">
              <Icon name="Info" size={16} />
              <span className="text-sm font-medium">Click on the document to place your signature</span>
            </div>
          </div>
        )}
      </div>
      {/* Document Navigation */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange('prev')}
          disabled={currentPage === 1}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Go to page:</span>
          <select
            value={currentPage}
            onChange={(e) => setCurrentPage(Number(e?.target?.value))}
            className="px-2 py-1 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange('next')}
          disabled={currentPage === totalPages}
          iconName="ChevronRight"
          iconPosition="right"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default DocumentViewer;